import { useState, useEffect } from "react";
import { useAuth } from "../App";

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockLogs = [
        {
          id: 1,
          user_name: "John Doe",
          username: "john.doe",
          action: "UPLOAD_MASTER_FILES",
          module: "MASTER_FILES",
          entity_type: "master_file",
          entity_id: 1,
          details: { fileType: "TAX", filename: "tax_2024.xlsx" },
          ip_address: "192.168.1.100",
          user_agent: "Mozilla/5.0...",
          created_at: "2024-12-15T10:30:00Z",
        },
        {
          id: 2,
          user_name: "Jane Smith",
          username: "jane.smith",
          action: "CREATE_JOB",
          module: "JOBS",
          entity_type: "job",
          entity_id: 5,
          details: { jobName: "Q4 Financial Report" },
          ip_address: "192.168.1.101",
          user_agent: "Mozilla/5.0...",
          created_at: "2024-12-15T09:15:00Z",
        },
        {
          id: 3,
          user_name: "Mike Johnson",
          username: "mike.johnson",
          action: "UPLOAD_AR04",
          module: "JOBS",
          entity_type: "ar04_file",
          entity_id: 3,
          details: { jobId: 5, filename: "ar04_december.xlsx" },
          ip_address: "192.168.1.102",
          user_agent: "Mozilla/5.0...",
          created_at: "2024-12-15T08:45:00Z",
        },
        {
          id: 4,
          user_name: "John Doe",
          username: "john.doe",
          action: "LOGIN",
          module: "AUTH",
          entity_type: null,
          entity_id: null,
          details: null,
          ip_address: "192.168.1.100",
          user_agent: "Mozilla/5.0...",
          created_at: "2024-12-15T08:00:00Z",
        },
        {
          id: 5,
          user_name: "Jane Smith",
          username: "jane.smith",
          action: "DELETE_USER",
          module: "USERS",
          entity_type: "user",
          entity_id: 10,
          details: { username: "old.user" },
          ip_address: "192.168.1.101",
          user_agent: "Mozilla/5.0...",
          created_at: "2024-12-14T16:20:00Z",
        },
      ];

      setLogs(mockLogs);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      // API call would go here
      console.log("Exporting activity logs...");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const getActionDescription = (action) => {
    const actionMap = {
      LOGIN: "เข้าสู่ระบบ",
      LOGOUT: "ออกจากระบบ",
      CREATE_JOB: "สร้างงาน",
      UPDATE_JOB: "แก้ไขงาน",
      DELETE_JOB: "ลบงาน",
      UPLOAD_MASTER_FILES: "อัปโหลด Master Files",
      UPLOAD_AR04: "อัปโหลด AR04",
      CREATE_USER: "สร้างผู้ใช้",
      UPDATE_USER: "แก้ไขผู้ใช้",
      DELETE_USER: "ลบผู้ใช้",
      EXPORT_DATA: "ส่งออกข้อมูล",
    };
    return actionMap[action] || action;
  };

  const getActionIcon = (action) => {
    const iconMap = {
      LOGIN: "🚪",
      LOGOUT: "🚶",
      CREATE_JOB: "💼",
      UPDATE_JOB: "✏️",
      DELETE_JOB: "🗑️",
      UPLOAD_MASTER_FILES: "📁",
      UPLOAD_AR04: "📊",
      CREATE_USER: "👤",
      UPDATE_USER: "✏️",
      DELETE_USER: "❌",
      EXPORT_DATA: "📥",
    };
    return iconMap[action] || "📝";
  };

  const getActionColor = (action) => {
    if (action.includes("DELETE")) return "#ef4444";
    if (action.includes("CREATE")) return "#10b981";
    if (action.includes("UPDATE")) return "#f59e0b";
    if (action.includes("UPLOAD")) return "#6366f1";
    if (action.includes("LOGIN")) return "#3b82f6";
    return "#64748b";
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.module?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = actionFilter === "all" || log.action === actionFilter;
    const matchesUser = userFilter === "all" || log.username === userFilter;

    let matchesDate = true;
    if (dateRange.startDate && dateRange.endDate) {
      const logDate = new Date(log.created_at).toISOString().split("T")[0];
      matchesDate =
        logDate >= dateRange.startDate && logDate <= dateRange.endDate;
    }

    return matchesSearch && matchesAction && matchesUser && matchesDate;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLogs = filteredLogs.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Get unique users and actions for filters
  const uniqueUsers = [...new Set(logs.map((log) => log.username))];
  const uniqueActions = [...new Set(logs.map((log) => log.action))];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>กำลังโหลดบันทึกกิจกรรม...</p>
      </div>
    );
  }

  if (!hasPermission("canViewLogs")) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h2>ไม่มีสิทธิ์เข้าถึง</h2>
          <p>คุณไม่มีสิทธิ์ในการดูบันทึกกิจกรรม</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>บันทึกกิจกรรม</h1>
          <p>ติดตามและตรวจสอบกิจกรรมของผู้ใช้ในระบบ</p>
        </div>
        {hasPermission("canExportData") && (
          <button className="btn btn-success" onClick={exportLogs}>
            📥 ส่งออกบันทึก
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: "24px" }}>
        <div
          className="grid grid-cols-2"
          style={{ gap: "16px", marginBottom: "16px" }}
        >
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">ค้นหา</label>
            <input
              type="text"
              className="form-input"
              placeholder="ชื่อผู้ใช้, การกระทำ, หรือโมดูล..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2" style={{ gap: "16px" }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">วันที่เริ่มต้น</label>
              <input
                type="date"
                className="form-input"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange({ ...dateRange, startDate: e.target.value })
                }
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">วันที่สิ้นสุด</label>
              <input
                type="date"
                className="form-input"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange({ ...dateRange, endDate: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3" style={{ gap: "16px" }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">การกระทำ</label>
            <select
              className="form-select"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="all">การกระทำทั้งหมด</option>
              {uniqueActions.map((action) => (
                <option key={action} value={action}>
                  {getActionDescription(action)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">ผู้ใช้</label>
            <select
              className="form-select"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="all">ผู้ใช้ทั้งหมด</option>
              {uniqueUsers.map((username) => (
                <option key={username} value={username}>
                  {username}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "end" }}>
            <button className="btn btn-outline" onClick={fetchLogs}>
              🔄 รีเฟรช
            </button>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            กิจกรรมล่าสุด ({filteredLogs.length.toLocaleString()} รายการ)
          </h3>
        </div>

        <div style={{ maxHeight: "600px", overflowY: "auto" }}>
          {paginatedLogs.length > 0 ? (
            paginatedLogs.map((log) => (
              <div
                key={log.id}
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "16px",
                  borderBottom: "1px solid #f1f5f9",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: `${getActionColor(log.action)}20`,
                    color: getActionColor(log.action),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                >
                  {getActionIcon(log.action)}
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                        {log.user_name} • {getActionDescription(log.action)}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          marginBottom: "8px",
                        }}
                      >
                        โมดูล: {log.module}
                        {log.entity_type && <> • ประเภท: {log.entity_type}</>}
                        {log.entity_id && <> • ID: {log.entity_id}</>}
                      </div>
                      {log.details && (
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          <details>
                            <summary style={{ cursor: "pointer" }}>
                              รายละเอียด
                            </summary>
                            <pre
                              style={{
                                background: "#f8fafc",
                                padding: "8px",
                                borderRadius: "4px",
                                marginTop: "4px",
                                fontSize: "11px",
                                overflow: "auto",
                              }}
                            >
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#94a3b8",
                          marginTop: "4px",
                        }}
                      >
                        IP: {log.ip_address}
                      </div>
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: "14px", fontWeight: "500" }}>
                        {new Date(log.created_at).toLocaleDateString("th-TH")}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {new Date(log.created_at).toLocaleTimeString("th-TH")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              ไม่พบกิจกรรมที่ตรงกับเงื่อนไขการค้นหา
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              padding: "20px",
            }}
          >
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              «
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            <span style={{ padding: "6px 12px", fontSize: "14px" }}>
              หน้า {currentPage} จาก {totalPages}
            </span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
